"use client";

import { cn } from "@/lib/utils";

interface LogoProps {
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  animate?: boolean;
}

const sizeMap = {
  sm: "w-6 h-6",
  md: "w-8 h-8",
  lg: "w-12 h-12",
  xl: "w-24 h-24",
};

/**
 * BlockyNetworks Logo — Connected blocks forming a network topology.
 * Four blocks at cardinal positions connected by lines, representing
 * server discovery and interconnected communities.
 */
export function Logo({ className, size = "md", animate = true }: LogoProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        sizeMap[size],
        "transition-transform duration-300 hover:scale-110 hover:rotate-[5deg]",
        className
      )}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="bn-grad-1" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#0ea5e9" />
        </linearGradient>
        <linearGradient id="bn-grad-2" x1="48" y1="0" x2="0" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#06b6d4" />
        </linearGradient>
      </defs>

      {/* Connection lines */}
      <line x1="24" y1="10" x2="10" y2="24" stroke="url(#bn-grad-2)" strokeWidth="2" strokeLinecap="round"
        className={animate ? "animate-[bn-line-draw_0.6s_ease-out_0.3s_both]" : ""}
        strokeDasharray="20" strokeDashoffset="0"
      />
      <line x1="24" y1="10" x2="38" y2="24" stroke="url(#bn-grad-2)" strokeWidth="2" strokeLinecap="round"
        className={animate ? "animate-[bn-line-draw_0.6s_ease-out_0.4s_both]" : ""}
        strokeDasharray="20" strokeDashoffset="0"
      />
      <line x1="10" y1="24" x2="24" y2="38" stroke="url(#bn-grad-2)" strokeWidth="2" strokeLinecap="round"
        className={animate ? "animate-[bn-line-draw_0.6s_ease-out_0.5s_both]" : ""}
        strokeDasharray="20" strokeDashoffset="0"
      />
      <line x1="38" y1="24" x2="24" y2="38" stroke="url(#bn-grad-2)" strokeWidth="2" strokeLinecap="round"
        className={animate ? "animate-[bn-line-draw_0.6s_ease-out_0.6s_both]" : ""}
        strokeDasharray="20" strokeDashoffset="0"
      />

      {/* Top node */}
      <rect x="18" y="4" width="12" height="12" rx="2.5" fill="url(#bn-grad-1)"
        className={animate ? "animate-[bn-node-pop_0.4s_ease-out_0.1s_both]" : ""}
      />
      {/* Left node */}
      <rect x="2" y="18" width="12" height="12" rx="2.5" fill="url(#bn-grad-1)" opacity="0.85"
        className={animate ? "animate-[bn-node-pop_0.4s_ease-out_0.2s_both]" : ""}
      />
      {/* Right node */}
      <rect x="34" y="18" width="12" height="12" rx="2.5" fill="url(#bn-grad-1)" opacity="0.85"
        className={animate ? "animate-[bn-node-pop_0.4s_ease-out_0.3s_both]" : ""}
      />
      {/* Bottom node */}
      <rect x="18" y="32" width="12" height="12" rx="2.5" fill="url(#bn-grad-1)"
        className={animate ? "animate-[bn-node-pop_0.4s_ease-out_0.4s_both]" : ""}
      />

      {/* Center pulse dot */}
      <circle cx="24" cy="24" r="3" fill="#34d399"
        className={animate ? "animate-[bn-pulse_2s_ease-in-out_1s_infinite]" : ""}
      />

      <style>{`
        @keyframes bn-node-pop {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes bn-line-draw {
          from { stroke-dashoffset: 20; opacity: 0; }
          to { stroke-dashoffset: 0; opacity: 1; }
        }
        @keyframes bn-pulse {
          0%, 100% { opacity: 1; r: 3; }
          50% { opacity: 0.5; r: 4; }
        }
      `}</style>
    </svg>
  );
}

export function LogoWithText({ className, size = "md", animate = true }: LogoProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Logo size={size} animate={animate} />
      <span
        className={cn(
          "font-bold tracking-tight bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent",
          size === "sm" && "text-lg",
          size === "md" && "text-xl",
          size === "lg" && "text-2xl",
          size === "xl" && "text-4xl"
        )}
      >
        BlockyNetworks
      </span>
    </div>
  );
}
